import { safeApiRequest, apiRequest } from './apiClient';
import { sanitizeSearchInput } from '../utils/security';

const CATEGORIES_ENDPOINT = '/api/categories';

/**
 * Kategori payload'ını oluşturur (create için)
 */
const buildCategoryPayload = (payload = {}) => {
  const data = {};

  if (payload.kategoriAdi !== undefined && payload.kategoriAdi !== '') {
    data.kategoriAdi = String(payload.kategoriAdi).trim();
  }

  if (payload.kategoriTipi !== undefined) {
    data.kategoriTipi = String(payload.kategoriTipi).trim();
  }

  if (payload.ustKategoriId !== undefined && payload.ustKategoriId !== null && payload.ustKategoriId !== '') {
    data.ustKategoriId = payload.ustKategoriId;
  }

  if (payload.kategoriSirasi !== undefined && payload.kategoriSirasi !== null) {
    data.kategoriSirasi = parseInt(payload.kategoriSirasi, 10);
  }

  if (payload.malKodu !== undefined) {
    const t = String(payload.malKodu ?? '').trim();
    data.malKodu = t === '' ? null : t;
  }
  if (payload.modelKodu !== undefined) {
    const t = String(payload.modelKodu ?? '').trim();
    data.modelKodu = t === '' ? null : t;
  }

  return data;
};

/**
 * Kategori güncelleme payload'ını oluşturur (update için - kategoriTipi hariç)
 */
const buildCategoryUpdatePayload = (payload = {}) => {
  const data = {};

  if (payload.kategoriAdi !== undefined && payload.kategoriAdi !== '') {
    data.kategoriAdi = String(payload.kategoriAdi).trim();
  }

  // kategoriTipi güncellemede gönderilmez (değiştirilemez)
  // ustKategoriId güncellemede gönderilmez (taşıma için ayrı endpoint var)
  
  // kategoriSirasi: 0 da geçerli bir değer olduğu için !== undefined && !== null kontrolü yeterli
  if (payload.kategoriSirasi !== undefined && payload.kategoriSirasi !== null) {
    const sirasi = parseInt(payload.kategoriSirasi, 10);
    if (!isNaN(sirasi)) {
      data.kategoriSirasi = sirasi;
    }
  }

  if (payload.malKodu !== undefined) {
    const t = String(payload.malKodu ?? '').trim();
    data.malKodu = t === '' ? null : t;
  }
  if (payload.modelKodu !== undefined) {
    const t = String(payload.modelKodu ?? '').trim();
    data.modelKodu = t === '' ? null : t;
  }

  return data;
};

/**
 * Kategorileri getirir.
 * ustKategoriId: `'root'` kökler; yoksa üst kategori id (doğrudan çocuklar). Parametre verilmezse tüm kategoriler.
 */
export const fetchCategories = async ({ kategoriTipi, ustKategoriId } = {}) => {
  const queryParams = {};

  if (kategoriTipi) {
    queryParams.kategoriTipi = String(kategoriTipi).trim();
  }
  if (ustKategoriId !== undefined && ustKategoriId !== null && ustKategoriId !== '') {
    queryParams.ustKategoriId = String(ustKategoriId).trim();
  }

  return safeApiRequest(CATEGORIES_ENDPOINT, {
    method: 'GET',
    query: queryParams,
    skipCsrf: true,
  });
};

/**
 * Kategorinin ana grubunu (root) getirir
 * ustKategoriId null olana kadar yukarı çıkar
 */
export const fetchCategoryRoot = async (categoryId) => {
  if (!categoryId) {
    throw new Error('Kategori ID gereklidir');
  }
  return safeApiRequest(`${CATEGORIES_ENDPOINT}/${categoryId}/root`, {
    method: 'GET',
    skipCsrf: true,
  });
};

/**
 * Kategori detayı
 */
export const fetchCategoryDetail = async (id) => {
  if (!id) {
    throw new Error('Kategori ID gereklidir');
  }
  return safeApiRequest(`${CATEGORIES_ENDPOINT}/${id}`, {
    method: 'GET',
    skipCsrf: true,
  });
};

/**
 * Kategori oluşturur
 */
export const createCategory = async (payload) => {
  const sanitizedPayload = buildCategoryPayload(payload);
  console.log('[CategoriesService] Creating category:', {
    endpoint: CATEGORIES_ENDPOINT,
    method: 'POST',
    payload: sanitizedPayload
  });
  
  return safeApiRequest(CATEGORIES_ENDPOINT, {
    method: 'POST',
    body: sanitizedPayload,
    skipCsrf: false, // POST için CSRF token gerekli
  });
};

/**
 * Kategori günceller
 */
export const updateCategory = async (id, payload) => {
  if (!id) {
    throw new Error('Kategori ID gereklidir');
  }
  const sanitizedPayload = buildCategoryUpdatePayload(payload);
  console.log('[CategoriesService] Updating category:', {
    endpoint: `${CATEGORIES_ENDPOINT}/${id}`,
    method: 'PUT',
    payload: sanitizedPayload,
    originalPayload: payload
  });
  
  const response = await safeApiRequest(`${CATEGORIES_ENDPOINT}/${id}`, {
    method: 'PUT',
    body: sanitizedPayload,
    skipCsrf: false, // PUT için CSRF token gerekli
  });
  
  console.log('[CategoriesService] Update response:', {
    basarili: response?.basarili,
    kategoriSirasi: response?.veri?.kategori?.kategoriSirasi,
    fullResponse: response
  });
  
  return response;
};

/**
 * Kategori siler
 */
export const deleteCategory = async (id) => {
  if (!id) {
    throw new Error('Kategori ID gereklidir');
  }
  return safeApiRequest(`${CATEGORIES_ENDPOINT}/${id}`, {
    method: 'DELETE',
    skipCsrf: false, // DELETE için CSRF token gerekli
  });
};

/**
 * Kategori taşır (kategoriyi başka bir kategorinin altına taşır)
 */
export const moveCategory = async (kategoriId, hedefUstKategoriId) => {
  if (!kategoriId) {
    throw new Error('Kategori ID gereklidir');
  }
  
  const payload = {};
  if (hedefUstKategoriId !== null && hedefUstKategoriId !== undefined && hedefUstKategoriId !== '') {
    payload.ustKategoriId = hedefUstKategoriId;
  } else {
    payload.ustKategoriId = null; // Ana kategori yapmak için
  }
  
  console.log('[CategoriesService] Moving category:', {
    endpoint: `${CATEGORIES_ENDPOINT}/${kategoriId}/move`,
    method: 'PATCH',
    payload
  });
  
  return safeApiRequest(`${CATEGORIES_ENDPOINT}/${kategoriId}/move`, {
    method: 'PATCH',
    body: payload,
    skipCsrf: false, // PATCH için CSRF token gerekli
  });
};

